<script setup lang="ts">
import { email as validEmail } from '@vee-validate/rules';
import { Field, Form } from 'vee-validate';
import { ref } from 'vue';
import { useStore } from 'vuex';

import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';

const emit = defineEmits(['step-complete']);

const store = useStore();
const { createdFromOrderUpload, customerEmail, loadSafcToken, setCustomerEmail } = useCart(store);
const { prepareCartForCheckout, resetCheckout } = useCheckout(store);
const { loadCustomerInfo } = useCustomer();
const email = ref(customerEmail.value ?? '');
const isLoading = ref(false);

const { errorMessages, validatorFailed } = useForm();
const validator: FlexibleMessageValidator<string> = (value) =>
  validEmail(value) || errorMessages.email.email;

const searchCustomerInfo = async () => {
  if (email.value) {
    try {
      await Promise.all([loadCustomerInfo(email.value), loadSafcToken(email.value)]);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }
};

const handleContinue = async () => {
  isLoading.value = true;
  if (!createdFromOrderUpload.value) {
    await resetCheckout();
  }
  await prepareCartForCheckout();
  await searchCustomerInfo();
  await setCustomerEmail(email.value);
  isLoading.value = false;
  emit('step-complete');
};
</script>

<template>
  <div data-test="search-customer-by-email">
    <div class="flex items-center">
      <div class="text-lg font-semibold md:text-xl">Email</div>
      <div class="ml-1 text-sm md:text-base text-neutral-500">(Optional)</div>
    </div>
    <Form v-slot="{ handleSubmit, meta: formMeta }" as="div">
      <div class="flex items-center mt-4 mb-6">
        <Field
          v-model="email"
          name="searchCustomerEmail"
          :rules="validator"
          v-slot="{ errors, field, meta }"
        >
          <div class="flex-1">
            <Input
              v-bind="field"
              type="text"
              id="searchCustomerEmail"
              placeholder="Email Address"
              aria-label="Customer Email Address"
              :invalid="validatorFailed(meta)"
              data-test="search-customer-email"
            />
            <InlineError
              data-test="search-customer-email-validation-error"
              :errors
              fieldName="Customer Email"
              :invalid="validatorFailed(meta)"
            />
          </div>
        </Field>
      </div>
      <ThemedButton
        data-test="search-customer-email-continue"
        :disabled="validatorFailed(formMeta)"
        :isLoading
        theme="gray"
        @click="handleSubmit(handleContinue)"
      >
        Continue
      </ThemedButton>
    </Form>
  </div>
</template>
